import type { MasteryMapDocument, MasteryMapListItem } from '@ts-mastery-map/interfaces'
import type { MasteryDashboardData } from '@app/types/mastery-map-dashboard/interfaces'
import type { ScopeMode } from '@utils/stores/scoped-entry'

import { useMasteryMapDashboardStore } from '@stores/mastery-map-dashboard'

import { useCsRoutes } from '@composables/routes/cs-route.hook'

import { isNotFoundError } from '@utils/pages/error'

interface MasteryMapDashboardApiReturn {
  fetchMasteryDashboard: (disciplineId: number, mode: ScopeMode) => Promise<void>
}

export const useMasteryMapDashboardApi = (): MasteryMapDashboardApiReturn => {
  const { $axios } = useNuxtApp()
  const dashboardStore = useMasteryMapDashboardStore()

  const requestList = async (disciplineId: number): Promise<MasteryMapListItem[]> => {
    const url = useCsRoutes('disciplines.mastery-maps', { '[discipline_id]': disciplineId })
    if (!url) throw new Error('Missing mastery maps request URL.')

    const { data } = await $axios.get(url)

    return (data?.data ?? []) as MasteryMapListItem[]
  }

  /** A listed test whose map is not generated yet answers 404; the dashboard leaves it out. */
  const requestDocument = async (disciplineId: number, assessmentId: number): Promise<MasteryMapDocument | null> => {
    const url = useCsRoutes('disciplines.mastery-map', {
      '[discipline_id]': disciplineId,
      '[assessment_id]': assessmentId,
    })
    if (!url) throw new Error('Missing mastery map request URL.')

    try {
      const { data } = await $axios.get(url)

      return (data?.data ?? null) as MasteryMapDocument | null
    } catch (err: unknown) {
      if (isNotFoundError(err)) return null

      throw err
    }
  }

  const requestDashboard = async (disciplineId: number): Promise<MasteryDashboardData> => {
    const list = await requestList(disciplineId)
    const documents = await Promise.all(list.map(item => requestDocument(disciplineId, item.assessment_id)))

    return {
      list,
      documents: Object.fromEntries(list.flatMap((item, index) => {
        const document = documents.at(index)

        return document ? [[item.assessment_id, document]] : []
      })),
    }
  }

  const fetchMasteryDashboard = async (disciplineId: number, mode: ScopeMode): Promise<void> => {
    const requestId = dashboardStore.begin(disciplineId, mode)

    try {
      dashboardStore.settle(disciplineId, requestId, await requestDashboard(disciplineId))
    } catch (err: unknown) {
      console.error('unable to retrieve mastery-map/mastery-map-dashboard.hook.ts:', err)

      dashboardStore.fail(disciplineId, requestId)
    }
  }

  return { fetchMasteryDashboard }
}
